import { useMemo, useState } from "react";
import { Layers, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { start: "", end: "", application: "UCaaS", carrier_status: "Active", carrier: "", treatment: "", rate_center: "", rate_center_state: "", cost: 0, domain: "", sbc: "", sms_enabled: false, e911_registered: false, billing: false };

export function expandRange(start, end) {
  if (!start || !end) return { error: "Enter both the start and end numbers." };
  const s = String(start).trim();
  const hasPlus = s.startsWith("+");
  const startDigits = s.replace(/\D/g, "");
  const endDigits = String(end).replace(/\D/g, "");
  if (!startDigits || !endDigits) return { error: "I couldn't read those numbers." };
  const endFull = endDigits.length < startDigits.length
    ? startDigits.slice(0, startDigits.length - endDigits.length) + endDigits
    : endDigits;
  if (endFull.length !== startDigits.length) return { error: "The start and end numbers don't line up (different lengths)." };
  const a = parseInt(startDigits, 10), b = parseInt(endFull, 10);
  if (b < a) return { error: "The ending number is lower than the starting number." };
  const count = b - a + 1;
  if (count > 500) return { error: `That range covers ${count} numbers — bulk add is limited to 500 at a time.` };
  const prefix = hasPlus ? "+" : "";
  const numbers = [];
  for (let i = a; i <= b; i++) numbers.push(prefix + String(i).padStart(startDigits.length, "0"));
  return { numbers };
}

export function BulkAddDid({ open, onOpenChange, apps = [], carrierStatuses = [], sbcOptions = {}, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => { setF(EMPTY); setConflicts(null); setSaving(false); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const { numbers, error } = useMemo(() => expandRange(f.start, f.end), [f.start, f.end]);

  const submit = async () => {
    if (error) { toast.error(error); return; }
    setSaving(true); setConflicts(null);
    const payload = {
      numbers,
      application: f.application, carrier_status: f.carrier_status, carrier: f.carrier, treatment: f.treatment,
      rate_center: f.rate_center, rate_center_state: f.rate_center_state, cost: Number(f.cost) || 0,
      domain: f.domain, sbc: f.sbc, sms_enabled: f.sms_enabled, e911_registered: f.e911_registered, billing: f.billing,
    };
    try {
      const { data } = await api.post("/dids/bulk", payload);
      toast.success(`Added ${data.added} DIDs`);
      reset(); onOpenChange(false); onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.conflicts) setConflicts(detail.conflicts);
      else toast.error(formatApiErrorDetail(detail) || "Bulk add failed");
    } finally { setSaving(false); }
  };

  const sbcList = sbcOptions[f.rate_center_state] || [];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="did-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> Bulk add DIDs</DialogTitle>
          <DialogDescription>Add a contiguous block of numbers at once. Enter the first number and the last number (or just its trailing digits).</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Start number *</Label><Input value={f.start} onChange={(e) => set("start", e.target.value)} placeholder="+15875550100" data-testid="bulk-start" /></div>
          <div className="space-y-1.5"><Label>End number *</Label><Input value={f.end} onChange={(e) => set("end", e.target.value)} placeholder="0149 or +15875550149" data-testid="bulk-end" /></div>

          <div className="space-y-1.5"><Label>Application</Label>
            <Select value={f.application} onValueChange={(v) => set("application", v)}>
              <SelectTrigger data-testid="bulk-application"><SelectValue /></SelectTrigger>
              <SelectContent>{apps.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Carrier status</Label>
            <Select value={f.carrier_status} onValueChange={(v) => set("carrier_status", v)}>
              <SelectTrigger data-testid="bulk-carrier-status"><SelectValue /></SelectTrigger>
              <SelectContent>{carrierStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Carrier</Label><Input value={f.carrier} onChange={(e) => set("carrier", e.target.value)} placeholder="Bell" data-testid="bulk-carrier" /></div>
          <div className="space-y-1.5"><Label>Treatment</Label><Input value={f.treatment} onChange={(e) => set("treatment", e.target.value)} placeholder="AA / Queue…" data-testid="bulk-treatment" /></div>
          <div className="space-y-1.5"><Label>Rate center</Label><Input value={f.rate_center} onChange={(e) => set("rate_center", e.target.value)} data-testid="bulk-rate-center" /></div>
          <div className="space-y-1.5"><Label>Province/State</Label><Input value={f.rate_center_state} onChange={(e) => set("rate_center_state", e.target.value)} placeholder="AB" data-testid="bulk-rate-center-state" /></div>
          <div className="space-y-1.5"><Label>Domain</Label><Input value={f.domain} onChange={(e) => set("domain", e.target.value)} placeholder="northwind.uc.ca" data-testid="bulk-domain" /></div>
          <div className="space-y-1.5"><Label>SBC (Geo)</Label>
            {sbcList.length > 0 ? (
              <Select value={f.sbc} onValueChange={(v) => set("sbc", v)}>
                <SelectTrigger data-testid="bulk-sbc"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{sbcList.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Input value={f.sbc} onChange={(e) => set("sbc", e.target.value)} data-testid="bulk-sbc" />}
          </div>
          <div className="space-y-1.5"><Label>Cost ($)</Label><Input type="number" step="0.01" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="bulk-cost" /></div>
          <div className="col-span-2 flex flex-wrap gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.sms_enabled} onCheckedChange={(v) => set("sms_enabled", v)} data-testid="bulk-sms" /> SMS enabled</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.e911_registered} onCheckedChange={(v) => set("e911_registered", v)} data-testid="bulk-e911" /> 911 registered</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.billing} onCheckedChange={(v) => set("billing", v)} data-testid="bulk-billing" /> Billing</label>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm" data-testid="bulk-preview">
          {error ? <span className="text-muted-foreground">{error}</span>
            : <span>Will add <span className="font-semibold text-foreground">{numbers.length}</span> DIDs: <span className="font-mono">{numbers[0]}</span> → <span className="font-mono">{numbers[numbers.length - 1]}</span></span>}
        </div>

        {conflicts && (
          <div data-testid="bulk-conflict" className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
            <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Cancelled — nothing was added</div>
            <p>These numbers already exist, so the whole batch was stopped to avoid duplicates:</p>
            <p className="mt-1 font-mono text-xs font-medium">{conflicts.slice(0, 12).join(", ")}{conflicts.length > 12 ? ` +${conflicts.length - 12} more` : ""}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !!error} data-testid="bulk-submit">{saving ? "Adding…" : `Add ${error ? "" : numbers.length + " "}DIDs`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
